import { FieldValue, QueryField } from '../../../../../types/QueryBuilder';
import { Translate } from '../ValueEditors/valueUtils';
import { CLIENT_LIST_FIELD_NAMES, COUNTRY_FIELD_NAME } from './fieldConfig';

const COUNTRY_LABEL_PREFIX_RE = /^\d+\s*-\s*/;
const COUNTRY_PRIORITY = ['FRANCE', 'BELGIQUE', 'LUXEMBOURG', 'ALLEMAGNE', 'SUISSE', 'ITALIE', 'ESPAGNE', 'MONACO', 'ANDORRE'];
const COUNTRY_PRIORITY_ORDER = new Map(COUNTRY_PRIORITY.map((name, index) => [name, index]));

const STATUS_ALLOWED_LABELS: Record<string, string[]> = {
    CUSTOMER_STATUS_EMAIL: ['VALID', 'SOFT1', 'SOFT2', 'HARD', 'NON RENSEIGNE'],
    CUSTOMER_STATUS_SMS: ['VALID', 'HARD', 'NON RENSEIGNE'],
    CUSTOMER_STATUS_COURRIER: ['VALID', 'ADRESSE INVALIDE', 'NPAI', 'NON RENSEIGNE'],
};

const STATUS_LABEL_KEYS: Record<string, string> = {
    VALID: 'valid',
    'NON RENSEIGNE': 'notSpecified',
    'ADRESSE INVALIDE': 'invalid',
    HARD: 'invalid',
};

const SOFTBOUNCE_NAMES = ['SOFT1', 'SOFT2'];
const SOFTBOUNCE_MERGED_NAME = SOFTBOUNCE_NAMES.join('_');

const LEADING_NUMBER_RE = /^0*(\d+)\s*[.)-]/;

// Les identifiants de listes sont issus d'une séquence : l'ordre décroissant est celui des imports, du plus récent au plus ancien
function sortByMostRecentList(values: FieldValue[]): FieldValue[] {
    return [...values].sort((a, b) => (Number(b.name) || 0) - (Number(a.name) || 0));
}

function extractLeadingNumber(label: string): number | null {
    const match = label.match(LEADING_NUMBER_RE);
    return match ? parseInt(match[1], 10) : null;
}

function sortByKey(values: FieldValue[], keyOf: (label: string) => number | null): FieldValue[] {
    return values
        .map((value, index) => ({ value, key: keyOf(value.label), index }))
        .sort((a, b) => {
            if (a.key !== null && b.key !== null) return a.key - b.key;
            if (a.key !== null) return -1;
            if (b.key !== null) return 1;
            return a.index - b.index;
        })
        .map(({ value }) => value);
}

const statusLabelOf = (value: FieldValue) => value.label.trim().toUpperCase();

function resolveStatusValues(values: FieldValue[], allowedLabels: string[], t: Translate): FieldValue[] {
    const order = new Map(allowedLabels.map((label, index) => [label, index]));
    const sorted = values
        .filter(v => order.has(statusLabelOf(v)))
        .sort((a, b) => order.get(statusLabelOf(a))! - order.get(statusLabelOf(b))!)
        .map(v => {
            const overrideKey = STATUS_LABEL_KEYS[statusLabelOf(v)];
            return overrideKey ? { ...v, label: t(`pages.clients.queryBuilder.simple.status.${overrideKey}`) } : v;
        });

    const isSoftbounce = (v: FieldValue) => SOFTBOUNCE_NAMES.includes(statusLabelOf(v));
    const firstSoftbounceIndex = sorted.findIndex(isSoftbounce);
    if (firstSoftbounceIndex === -1) return sorted;

    const withoutSoftbounce = sorted.filter(v => !isSoftbounce(v));
    withoutSoftbounce.splice(firstSoftbounceIndex, 0, {
        name: SOFTBOUNCE_MERGED_NAME,
        label: t('pages.clients.queryBuilder.simple.softbounce.label'),
        mergedNames: sorted.filter(isSoftbounce).map(v => v.name),
        tooltip: t('pages.clients.queryBuilder.simple.softbounce.tooltip'),
    });
    return withoutSoftbounce;
}

function resolveCountryValues(values: FieldValue[]): FieldValue[] {
    return values
        .map(v => ({ ...v, label: v.label.replace(COUNTRY_LABEL_PREFIX_RE, '') }))
        .sort((a, b) => {
            const orderA = COUNTRY_PRIORITY_ORDER.get(a.label) ?? Infinity;
            const orderB = COUNTRY_PRIORITY_ORDER.get(b.label) ?? Infinity;
            return orderA !== orderB ? orderA - orderB : a.label.localeCompare(b.label, 'fr');
        });
}

export function resolveFieldValues(field: QueryField, t: Translate): FieldValue[] {
    const usableValues = sortByKey((field.values ?? []).filter(v => v.name && v.label.trim()), extractLeadingNumber);

    const statusAllowedLabels = STATUS_ALLOWED_LABELS[field.name];
    if (statusAllowedLabels) return resolveStatusValues(usableValues, statusAllowedLabels, t);
    if (CLIENT_LIST_FIELD_NAMES.includes(field.name)) return sortByMostRecentList(usableValues);
    if (field.name === COUNTRY_FIELD_NAME) return resolveCountryValues(usableValues);
    return usableValues;
}
